import React from 'react';
import { Chip } from '@mui/material';
import { Text, Theme, i18n, tokens } from '@simplybenefits/sb-ui-library';
import { helpers } from "@simplybenefits/sb-utils";
import Tooltip from '@mui/material/Tooltip';
import { Info as InfoIcon } from '@mui/icons-material';
import UsageBlock from './UsageBlock.js';

/**
 * 
 * @param {*} tooltipText 
 * @param {*} text
 * @returns 
 */
const TextLine = ({ tooltipText, text }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Tooltip
                title={ tooltipText }
                arrow
                placement="top"
            >
                <InfoIcon aria-label="info" style={{ fontSize: 15, color: Theme.colors.mediumGrey, marginRight: 10 }} />
            </Tooltip>
            <Text small bold>{ text }</Text>
        </div>
    </div>
);

/**
 * @param usage
 * @param fillColor
 * @param waivedEHC
 * @returns {JSX.Element}
 * @constructor
 */
const ParamedicalsUsageBlock = ({ usage, fillColor, waivedEHC }) => {
    const {
        usageObject,
        combinedAmountRemaining,
        familyCombinedAmountRemaining,
        mentalWellnessCombinedAmountRemaining,
        functionalTreatmentCombinedAmountRemaining,
        isMentalWellnessIncluded,
        isFunctionalTreatmentIncluded,
        isFamilyCombinedMaximum,
        isCombinedMaximum,
        isByPractitionerToCombinedMaximum,
        mentalWellnessCombinedMax,
        functionalTreatmentCombinedMax,
        familyCombinedMaximum,
        combinedMaximum
    } = usage;

    return (
        <React.Fragment>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                <div>
                    <Text
                        medium
                        bold
                        customStyle={{
                            flex: 1,
                            display: 'inline',
                            margin: '0 0 10px 0'
                        }}
                    >
                        Paramedicals
                    </Text>

                    {
                        waivedEHC &&
                        <Chip
                            size="small"
                            label="Waived"
                            color="primary"
                            variant="outlined"
                            style={{
                                margin: "5px 10px",
                                borderRadius: 30,
                                borderWidth: 2,
                                borderColor: Theme.colors.lightGrey,
                                fontFamily: Theme.fonts.type.heading,
                                fontSize: 14,
                                color: '#666'
                            }}
                        />
                    }
                </div>

                <div style={{ marginBottom: 5 }}> 
                    {
                        isFamilyCombinedMaximum
                            ? (
                                <TextLine 
                                    tooltipText={i18n.t(tokens.labels.coverage.spendUpToAmountOnParamedicalServicesFamilyMaximum, { amount: helpers.numberWithCommasDecimals(familyCombinedAmountRemaining) })}
                                    text={ i18n.t(tokens.labels.coverage.combinedFamilyMaximumRemaining, { combinedMax: helpers.numberWithCommasDecimals(familyCombinedMaximum), remaining: helpers.numberWithCommasDecimals(familyCombinedAmountRemaining) }) }
                                />
                            )
                            : null
                    }

                    {
                        isCombinedMaximum || isByPractitionerToCombinedMaximum
                            ? (
                                <TextLine 
                                    tooltipText={ i18n.t(tokens.labels.coverage.spendUpToAmountOnParamedicalServices, { amount: helpers.numberWithCommasDecimals(combinedAmountRemaining) })}
                                    text={ i18n.t(tokens.labels.coverage.combinedMaximumRemaining, { combinedMax: helpers.numberWithCommasDecimals(combinedMaximum), remaining: helpers.numberWithCommasDecimals(combinedAmountRemaining) })}
                                />
                            )
                            : null
                    }
                </div>
            </div>

            {
                Object.entries(usageObject.paramedical).map(([ key, item ]) => {
                    if (item.showItem) {
                        return (
                            <UsageBlock
                                key={ key }
                                usage={{
                                    type: item.title,
                                    limit: item.limit,
                                    used: item.used
                                }}
                                fillColor={ fillColor }
                                showRemaining={ !isCombinedMaximum && !isByPractitionerToCombinedMaximum }
                            />
                        );
                    }

                    return null;
                })
            }

            {
                isMentalWellnessIncluded &&
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                        <Text
                            medium
                            bold
                            customStyle={{
                                flex: 1,
                                display: 'inline',
                                margin: '0 0 10px 0'
                            }}
                        >
                            Mental Wellness
                        </Text>

                        {
                            waivedEHC &&
                            <Chip
                                size="small"
                                label="Waived"
                                color="primary"
                                variant="outlined"
                                style={{
                                    margin: "5px 10px",
                                    borderRadius: 30,
                                    borderWidth: 2,
                                    borderColor: Theme.colors.lightGrey,
                                    fontFamily: Theme.fonts.type.heading,
                                    fontSize: 14,
                                    color: '#666'
                                }}
                            />
                        }

                        <TextLine 
                            tooltipText={ i18n.t(tokens.labels.coverage.spendUpToAmountOnMentalWellnessServices, { amount: helpers.numberWithCommasDecimals(mentalWellnessCombinedAmountRemaining) })}
                            text={ i18n.t(tokens.labels.coverage.combinedMaximumRemaining, { combinedMax: helpers.numberWithCommasDecimals(mentalWellnessCombinedMax), remaining: helpers.numberWithCommasDecimals(mentalWellnessCombinedAmountRemaining) })}
                        />
                    </div>

                    {
                        Object.entries(usageObject.mentalWellness).map(([ key, item ]) => (
                            <UsageBlock
                                key={ key }
                                usage={{
                                    type: item.title,
                                    limit: item.limit,
                                    used: item.used
                                }}
                                fillColor={ fillColor }
                                showRemaining={ !isMentalWellnessIncluded }
                            />
                        ))
                    }
                </>
            }

            {
                isFunctionalTreatmentIncluded &&
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                        <Text
                            medium
                            bold
                            customStyle={{
                                flex: 1,
                                display: 'inline',
                                margin: '0 0 10px 0'
                            }}
                        >
                            Functional Treatment
                        </Text>

                        {
                            waivedEHC &&
                            <Chip
                                size="small"
                                label="Waived"
                                color="primary"
                                variant="outlined"
                                style={{
                                    margin: "5px 10px",
                                    borderRadius: 30,
                                    borderWidth: 2,
                                    borderColor: Theme.colors.lightGrey,
                                    fontFamily: Theme.fonts.type.heading,
                                    fontSize: 14,
                                    color: '#666'
                                }}
                            />
                        }

                        <TextLine 
                            tooltipText={ i18n.t(tokens.labels.coverage.spendUpToAmountOnFunctionalTreatmentServices, { amount: helpers.numberWithCommasDecimals(functionalTreatmentCombinedAmountRemaining) })}
                            text={ i18n.t(tokens.labels.coverage.combinedMaximumRemaining, { combinedMax: helpers.numberWithCommasDecimals(functionalTreatmentCombinedMax), remaining: helpers.numberWithCommasDecimals(functionalTreatmentCombinedAmountRemaining) })}
                        />
                    </div>

                    {
                        Object.entries(usageObject.functionalTreatment).map(([ key, item ]) => (
                            <UsageBlock
                                key={ key }
                                usage={{
                                    type: item.title,
                                    limit: item.limit,
                                    used: item.used
                                }}
                                fillColor={ fillColor }
                                showRemaining={ !isFunctionalTreatmentIncluded }
                            />
                        ))
                    }
                </>
            }
        </React.Fragment>
    );
};

export default ParamedicalsUsageBlock;